import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const plans = [
  {
    name: "Free", price: 0, currency: "$", period: "forever", id: "free",
    features: ["50 AI credits/month", "All 7 niches", "Trend radar", "Unlimited drafts", "Watermarked exports"],
    cta: "Start free",
  },
  {
    name: "Pro", price: 1599, currency: "₹", period: "month", id: "pro", featured: true,
    features: ["500 AI credits/month", "Priority generation", "HD exports, no watermark", "Trend audio library", "Analytics dashboard", "Email support"],
    cta: "Upgrade to Pro",
  },
  {
    name: "Premium", price: 4099, currency: "₹", period: "month", id: "premium",
    features: ["2,000 AI credits/month", "Bulk content batching", "Competitor analysis", "Auto B-roll suggestions", "AI voiceovers", "Priority support"],
    cta: "Go Premium",
  },
];

const formatPrice = (price, currency) => {
  if (price === 0) return "0";
  return `${currency}${price.toLocaleString("en-IN")}`;
};

const RAZORPAY_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const s = document.createElement("script");
    s.src = RAZORPAY_SCRIPT;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Pricing() {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState("");
  const nav = useNavigate();

  // Pre-warm Razorpay script
  useEffect(() => { loadRazorpay(); }, []);

  const checkout = async (planId) => {
    if (!user) { window.location.href = "/auth?mode=signup"; return; }
    if (planId === "free") return;
    setLoading(planId);
    try {
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load Razorpay. Check your connection."); setLoading(""); return; }

      const { data } = await api.post("/billing/checkout", {
        package_id: planId, origin_url: window.location.origin,
      });

      const opts = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "ClipForge AI",
        description: `${planId === "pro" ? "Pro" : "Premium"} subscription`,
        order_id: data.order_id,
        prefill: {
          name: data.user?.name || "",
          email: data.user?.email || "",
        },
        theme: { color: "#FF3B6B" },
        handler: async (resp) => {
          try {
            await api.post("/billing/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            await refreshUser();
            toast.success(`Subscription active! Welcome to ${planId.toUpperCase()}`);
            setTimeout(() => nav("/app"), 1200);
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Payment verification failed");
          } finally {
            setLoading("");
          }
        },
        modal: {
          ondismiss: () => {
            setLoading("");
            toast.info("Payment cancelled");
          },
        },
      };

      const rz = new window.Razorpay(opts);
      rz.on("payment.failed", (resp) => {
        const reason = resp?.error?.description || "Payment failed";
        toast.error(reason);
        setLoading("");
      });
      rz.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setLoading("");
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="pricing-page">
      <Navbar />
      <section className="pt-32 px-6 max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-[#FF3B6B] text-sm font-mono mb-3">// PRICING</p>
          <h1 className="text-5xl sm:text-6xl font-bold tracking-tighter">Built for every<br />stage of creator.</h1>
          <p className="mt-6 text-white/60">Start free. Upgrade when your views demand more credits. Cancel anytime.</p>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((p) => (
            <div key={p.id} className={`relative rounded-2xl p-8 ${p.featured ? "border-2 border-[#FF3B6B] bg-[#FF3B6B]/5" : "glass"}`} data-testid={`plan-${p.id}`}>
              {p.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FF3B6B] text-xs px-3 py-1 rounded-full font-semibold">MOST POPULAR</div>
              )}
              <h3 className="text-xl font-semibold">{p.name}</h3>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-5xl font-bold tracking-tighter">{p.price === 0 ? "Free" : formatPrice(p.price, p.currency)}</span>
                {p.price > 0 && <span className="text-white/50 text-sm">/{p.period}</span>}
              </div>
              <ul className="mt-8 space-y-3">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-white/80">
                    <Check weight="bold" className="text-[#FF3B6B] mt-0.5 shrink-0" size={16} /> {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => checkout(p.id)}
                disabled={loading === p.id || (user?.plan === p.id)}
                className={`mt-8 w-full py-3 rounded-lg font-semibold transition-all disabled:opacity-50 ${p.featured ? "btn-primary" : "bg-white text-black hover:bg-white/90"}`}
                data-testid={`plan-cta-${p.id}`}
              >
                {user?.plan === p.id ? "Current plan" : loading === p.id ? "Opening checkout..." : p.cta}
              </button>
            </div>
          ))}
        </div>

        <div className="mt-16 glass rounded-xl p-6 text-center text-sm text-white/50 flex items-center justify-center gap-2">
          <Lightning size={16} className="text-[#EAB308]" /> Powered by Razorpay · UPI, Cards, Netbanking, Wallets supported
        </div>
      </section>
      <Footer />
    </div>
  );
}
